import request from '@/utils/request'

/**
 * 获取告警规则列表
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum=1] 页码，默认为1
 * @param {number} [params.pageSize=10] 每页条数，默认为10
 * @param {string} [params.ruleName] 规则名称
 * @param {string} [params.ruleType] 规则类型
 * @param {number} [params.status] 状态：1-启用，0-禁用
 * @param {number} [params.userId] 用户ID
 * @param {number} [params.bridgeId] 桥梁ID
 * @param {number} [params.areaId] 区域ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       ruleName: string,
 *       ruleType: string,
 *       ruleCondition: string,
 *       alertLevel: string,
 *       alertMessage: string,
 *       alertMethods: string,
 *       alertAction: string,
 *       detectionMethod: string,
 *       priority: number,
 *       status: number,
 *       areaId: number,
 *       bridgeId: number,
 *       userId: number,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的告警规则列表
 */
export function getAlertRules(params) {
  return request({
    url: '/alert-rule/list',
    method: 'get',
    params
  })
}

/**
 * 创建告警规则
 * @param {Object} data 告警规则数据
 * @param {string} data.ruleName 规则名称
 * @param {string} data.ruleType 规则类型
 * @param {string} data.ruleCondition 规则条件(JSON格式)
 * @param {string} data.alertLevel 告警级别
 * @param {string} data.alertMessage 告警消息模板
 * @param {string} data.alertMethods 告警方式，多个值用逗号分隔，如：VHF,WECHAT,PAGE
 * @param {string} [data.alertAction] 告警动作(JSON格式)
 * @param {string} [data.detectionMethod] 检测方法：ENTRY-进入检测，CROSSING-穿越检测等
 * @param {number} [data.priority] 优先级(1-5，1最高)
 * @param {number} [data.status] 状态：1-启用，0-禁用
 * @param {number} [data.areaId] 关联区域ID
 * @param {number} [data.bridgeId] 关联桥梁ID
 * @param {number} [data.userId] 关联用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     ruleName: string,
 *     ruleType: string,
 *     ruleCondition: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     alertMethods: string,
 *     alertAction: string,
 *     detectionMethod: string,
 *     priority: number,
 *     status: number,
 *     areaId: number,
 *     bridgeId: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的告警规则
 */
export function createAlertRule(data) {
  return request({
    url: '/alert-rule',
    method: 'post',
    data
  })
}

/**
 * 更新告警规则
 * @param {number} id 告警规则ID
 * @param {Object} data 告警规则数据
 * @param {string} data.ruleName 规则名称
 * @param {string} data.ruleType 规则类型
 * @param {string} data.ruleCondition 规则条件(JSON格式)
 * @param {string} data.alertLevel 告警级别
 * @param {string} data.alertMessage 告警消息模板
 * @param {string} data.alertMethods 告警方式，多个值用逗号分隔，如：VHF,WECHAT,PAGE
 * @param {string} [data.alertAction] 告警动作(JSON格式)
 * @param {string} [data.detectionMethod] 检测方法：ENTRY-进入检测，CROSSING-穿越检测等
 * @param {number} [data.priority] 优先级(1-5，1最高)
 * @param {number} [data.status] 状态：1-启用，0-禁用
 * @param {number} [data.areaId] 关联区域ID
 * @param {number} [data.bridgeId] 关联桥梁ID
 * @param {number} [data.userId] 关联用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     ruleName: string,
 *     ruleType: string,
 *     ruleCondition: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     alertMethods: string,
 *     alertAction: string,
 *     detectionMethod: string,
 *     priority: number,
 *     status: number,
 *     areaId: number,
 *     bridgeId: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的告警规则
 */
export function updateAlertRule(id, data) {
  return request({
    url: `/alert-rule/${id}`,
    method: 'put',
    data
  })
}

/**
 * 获取告警规则详情
 * @param {number} id 告警规则ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     ruleName: string,
 *     ruleType: string,
 *     ruleCondition: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     alertMethods: string,
 *     alertAction: string,
 *     detectionMethod: string,
 *     priority: number,
 *     status: number,
 *     areaId: number,
 *     bridgeId: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 告警规则详情
 */
export function getAlertRuleById(id) {
  return request({
    url: `/alert-rule/${id}`,
    method: 'get'
  })
}

/**
 * 删除告警规则
 * @param {number} id 告警规则ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteAlertRule(id) {
  return request({
    url: `/alert-rule/${id}`,
    method: 'delete'
  })
}

/**
 * 更新告警规则状态
 * @param {number} id 告警规则ID
 * @param {number} status 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 更新结果
 */
export function updateAlertRuleStatus(id, status) {
  return request({
    url: `/alert-rule/${id}/status`,
    method: 'put',
    params: { status }
  })
}

/**
 * 获取告警级别选项
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     key: string,
 *     value: string
 *   }>
 * }>} 告警级别选项列表
 */
export function getAlertLevelOptions() {
  return request({
    url: '/alert-rule/level-options',
    method: 'get'
  })
}

/**
 * 获取告警方式选项
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     key: string,
 *     value: string
 *   }>
 * }>} 告警方式选项列表
 */
export function getAlertMethodOptions() {
  return request({
    url: '/alert-rule/method-options',
    method: 'get'
  })
}

/**
 * 获取规则类型选项
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     key: string,
 *     value: string
 *   }>
 * }>} 规则类型选项列表
 */
export function getRuleTypeOptions() {
  return request({
    url: '/alert-rule/type-options',
    method: 'get'
  })
}

/**
 * 获取告警规则模板列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     ruleName: string,
 *     ruleType: string,
 *     ruleCondition: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     alertMethods: string,
 *     alertAction: string,
 *     detectionMethod: string,
 *     priority: number,
 *     status: number,
 *     areaId: number,
 *     bridgeId: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 告警规则模板列表
 */
export function getAlertRuleTemplates() {
  return request({
    url: '/alert-rule/templates',
    method: 'get'
  })
}

/**
 * 复制现有告警规则
 * @param {number} id 告警规则ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     ruleName: string,
 *     ruleType: string,
 *     ruleCondition: string,
 *     alertLevel: string,
 *     alertMessage: string,
 *     alertMethods: string,
 *     alertAction: string,
 *     detectionMethod: string,
 *     priority: number,
 *     status: number,
 *     areaId: number,
 *     bridgeId: number,
 *     userId: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 复制后的告警规则
 */
export function copyAlertRule(id) {
  return request({
    url: `/alert-rule/${id}/copy`,
    method: 'post'
  })
} 

/**
 * 手动发送预警通知
 * @param {number} bridgeId 桥梁ID
 * @param {Object} data 预警信息数据
 * @param {integer} [data.alertLevel] 预警级别：1-低，2-中，3-高
 * @param {string} [data.alertTime] 预警时间，格式：YYYY-MM-DDThh:mm:ss.sssZ
 * @param {string} data.alertType 预警类型，如COURSE_DEVIATION、SPEED_OVER等
 * @param {integer} [data.areaId] 区域ID
 * @param {string} data.description 预警描述
 * @param {number} [data.latitude] 纬度
 * @param {number} [data.longitude] 经度
 * @param {Array<integer>} [data.userIds] 接收通知的用户ID列表
 * @param {string} [data.vesselMmsi] 船舶MMSI
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 发送结果
 */
export function sendManualAlert(bridgeId, data) {
  return request({
    url: '/alert/manual',
    method: 'post',
    params: { bridgeId },
    data
  })
} 